import React, { MediaHTMLAttributes, useCallback, useEffect, useRef } from "react";

import { css, SerializedStyles } from "@emotion/react";

import { CSS_MQ } from "../../styles/breakpoints";
import { VideoControlsLowerBar } from "./subcomponents/VideoControlsLowerBar";
import cn from "classnames";
import { useVideoCommandHandler } from "./use-video-command-handler";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { ComponentVideoType } from "@swo/storefront";
import { VideoIconsRenderer } from "./video-icons";
import { VideoPlaybackControl } from "./VideoPlaybackControl";
import { MeasuredVideoBox, SharedVideoProps } from "../HeroImageComponent/subcomponents/ResponsiveVideo";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { handleCommand } from "./commands";
import { useEqualState } from "../../hooks";
import { useCaptionsState } from "./captions";
import { SharedVideoPropsT } from "./types";
import type { VideoType } from "@swo/shared";

export type SimpleVideoPlayerProps = {
  withPlayerUI: boolean;
  resolutionPickerStrategy: "middle" | "ceil" | "double-ceil" | number;
  mode: "fill" | "contain";
  sharedVideoProps: SharedVideoPropsT;
  attachStyles?: SerializedStyles;
  wrapperClassName?: string;
  videoClassName?: string;
  video: VideoType["video"];
  subtitles: VideoType["subtitles"];
  portraitMode: boolean;
  responsiveVideo?: boolean;
  sys:
    | {
        id: string;
      }
    | undefined;
};

export const SwarovskiOptikVideoPlayer: ReactTFC<SimpleVideoPlayerProps> = props => {
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const [waiting, setWaiting] = useEqualState(false);

  const { exposedState, dispatchSync } = useVideoCommandHandler();

  const [captionsState, toggleCaptionsState] = useCaptionsState(props.subtitles);

  const onPlayCb = useCallback((ev: React.SyntheticEvent<HTMLVideoElement>) => {
    dispatchSync({ type: "is-playing" });
  }, []);

  const onPauseCb = useCallback(() => {
    dispatchSync({ type: "is-paused" });
  }, []);

  const onVolumeChangeCb = useCallback((ev: React.SyntheticEvent<HTMLVideoElement>) => {
    const desiredState = ev.currentTarget.muted ? 0 : 1;

    dispatchSync({ type: "changed-volume", value: desiredState });
  }, []);

  const onProgressCb = useCallback((ev: React.SyntheticEvent<HTMLVideoElement>) => {
    dispatchSync({ type: "changed-progress", value: ev.currentTarget.currentTime });
  }, []);

  const onTimeUpdate = useCallback((ev: React.SyntheticEvent<HTMLVideoElement>) => {
    const value = ev.currentTarget.currentTime;

    if (value > 0) {
      dispatchSync({ type: "changed-progress", value });
    }
  }, []);

  const onStalled = useCallback(() => {
    setWaiting(true);
  }, []);
  const onWaiting = useCallback(() => {
    setWaiting(true);
  }, []);
  const onCanPlay = useCallback(() => {
    setWaiting(false);
  }, []);

  const togglePlayMode = useCallback(() => {
    return handleCommand({ type: "toggle-play" }, playerRef);
  }, []);

  const setSoundLevelTo = useCallback((v: number) => {
    return handleCommand({ type: "set-volume", value: v }, playerRef).then(() => {
      // iOS Safari doesn't trigger onVolumeChanged event
      // because it doesn't even allow modifying volume...
      dispatchSync({ type: "changed-volume", value: v });
    });
  }, []);

  const setProgressTo = useCallback((p: number) => {
    return handleCommand({ type: "set-progress", value: p }, playerRef);
  }, []);

  const seekBy = useCallback((diff: number) => {
    return handleCommand({ type: "seek-by", value: diff }, playerRef);
  }, []);

  const fullscreenHandler = useCallback(() => {
    return handleCommand({ type: "toggle-fullscreen" }, playerRef);
  }, []);

  const handleKeyboardCb = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    switch (e.key) {
      case " ":
        return togglePlayMode();
      case "k":
        return togglePlayMode();
      case "m":
        return setSoundLevelTo(0);
      case "f":
        return fullscreenHandler();
      case "j":
        return seekBy(-0.1);
      case "l":
        return seekBy(0.1);
      case "ArrowLeft":
        return seekBy(-0.1);
      case "ArrowRight":
        return seekBy(0.1);
      case "Home":
        return setSoundLevelTo(0);
      case "End":
        return setSoundLevelTo(0.999);
    }
  };

  useEffect(() => {
    if (props.sharedVideoProps.autoPlay) {
      const current = playerRef?.current;

      if (current) {
        void current.play();
      }
    }
  }, [props.sharedVideoProps.autoPlay, props.video.url]);

  const componentStyles = [props.attachStyles, styles];

  const videoEvents: MediaHTMLAttributes<HTMLVideoElement> = {
    onPlay: onPlayCb,
    onLoadedMetadata: ev => {
      dispatchSync({
        type: "initialize",
        value: {
          soundLevel: ev.currentTarget.volume,
          currentTime: ev.currentTarget.currentTime,
        },
      });
    },
    onPause: onPauseCb,
    onVolumeChange: onVolumeChangeCb,
    onProgress: onProgressCb,
    onTimeUpdate: onTimeUpdate,
    onStalled: onStalled,
    onWaiting: onWaiting,
    onCanPlay: onCanPlay,
  };

  const sharedVideoProps: SharedVideoProps = {
    ...props.sharedVideoProps,
    playsInline:
      props.sharedVideoProps.playsInline === "enabled-after-interaction"
        ? exposedState.hasInteracted
        : props.sharedVideoProps.playsInline,
    muted:
      props.sharedVideoProps.muted === "disabled-after-interaction"
        ? !exposedState.hasInteracted
        : props.sharedVideoProps.muted,
  };

  const Inner = (
    <MeasuredVideoBox
      isFullscreen={exposedState.fullScreen}
      className={cn(props.videoClassName, `mode-${props.mode}`)}
      ref={playerRef}
      video={props.video}
      wrapperClassName={props.wrapperClassName || "visible"}
      measuredProgress={exposedState.currentTime || 0}
      measuredPlayingState={exposedState.state}
      resolutionPickerStrategy={props.resolutionPickerStrategy}
      sharedVideoProps={sharedVideoProps}
      events={videoEvents}
      responsiveVideo={props.responsiveVideo ?? true}
    >
      {props.subtitles && captionsState === "visible" && (
        <track src={props.subtitles.secure_url} label="" kind="subtitles" default />
      )}
    </MeasuredVideoBox>
  );

  return props.withPlayerUI ? (
    <div
      className={cn(
        exposedState.fullScreen ? "is-fullscreen" : undefined,
        `mode-${props.portraitMode ? "vertical" : "horizontal"}`,
        getComponentCMSClassname(props?.sys?.id),
      )}
      css={componentStyles}
    >
      <VideoIconsRenderer
        icon={"FullScreenSvgIcon"}
        css={fullScreenIconStyles}
        onClick={() => {
          void fullscreenHandler();
        }}
      />

      <div
        className={cn("_video-overlay", { waiting: waiting })}
        onClick={() => {
          void togglePlayMode();
        }}
        tabIndex={-1}
        onKeyDown={e => {
          void handleKeyboardCb(e);
        }}
        css={videoOverlayStyles}
      >
        <VideoPlaybackControl state={waiting ? "waiting" : exposedState.state} />
      </div>

      {Inner}

      <div
        className={cn("_video-bottom-bar", { absolute: !props.subtitles })}
        tabIndex={-1}
        onKeyDown={e => {
          void handleKeyboardCb(e);
        }}
        css={bottomBarWrapperStyles}
      >
        <VideoControlsLowerBar
          soundLevel={exposedState.soundLevel}
          duration={props.video.duration}
          currentTime={exposedState.currentTime}
          setSoundLevelTo={v => {
            void setSoundLevelTo(v);
          }}
          togglePlayMode={() => {
            void togglePlayMode();
          }}
          setProgressTo={p => {
            void setProgressTo(p);
          }}
          paused={exposedState.state === "paused"}
          captions={captionsState}
          toggleCaptions={toggleCaptionsState}
        />
      </div>
    </div>
  ) : (
    Inner
  );
};

const bottomBarWrapperStyles = css`
  &.absolute {
    position: absolute;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.5);
  }

  position: relative; // to be above overlay

  background: black;
  width: 100%;
  height: 50px;

  display: flex;
  justify-content: center;
  align-items: center;

  z-index: 3;

  svg {
    cursor: pointer;
    height: 100%;

    ${CSS_MQ.atMostSm} {
      width: 36px;
    }
  }
`;
const videoOverlayStyles = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  z-index: 2;

  cursor: pointer;

  .progress-icon {
    transition: 0.2s linear filter;
    filter: opacity(0);

    &:hover {
      filter: opacity(1) !important;
    }
  }

  &:hover {
    .progress-icon {
      transition: 0.2s linear filter;
      filter: opacity(0.7);
    }
  }
`;

const fullScreenIconStyles = css`
  position: absolute;
  z-index: 3;
  cursor: pointer;

  top: 0;
  right: 0;
  ${CSS_MQ.atLeastLg} {
    top: 15px;
    right: 15px;
  }

  padding: 15px;

  transition: 0.2s ease-out transform;

  &:hover {
    transition: 0.2s ease-out transform;
    transform: scale(1.05);
    transform-origin: center center;
  }

  width: 60px;
  height: 60px;
`;

const styles = css`
  width: 100%;
  position: relative;
  z-index: 80;

  background: black;

  transition: 0.2s ease-out transform;

  &.is-fullscreen {
    video {
      object-fit: contain;
    }
  }

  &.mode-horizontal {
    ${CSS_MQ.atLeastMd} {
      max-width: var(--max-content-width-new);
    }
  }

  &.mode-vertical {
    max-width: 640px;
  }

  margin-left: auto;
  margin-right: auto;
`;
